import { useEffect, useRef } from "react";
import useProfiler from "../../../hooks/useProfiler.js";
import { createTrackingUtils } from "../index.js";
import { flightsSessionStore } from "@flights/web-api-utils-universal";
import useRouteName from "../../../hooks/useRouteName.js";
import useGetSearchResultsBound from "../../../hooks/useGetSearchResultsBound.js";
const utils = createTrackingUtils({
    name: "flights_split_bounds_us",
    stages: {
        us_round_trip_users: 1,
        main: 2,
        desktop: 3,
        mobile: 4,
        direct: 5,
        meta: 6,
        Longhaul: 7,
        Weekend: 8,
        solo: 9
    },
    defaultStage: 1,
    goals: {
        change_departing_cta: 1,
        error_or_empty_inbountd: 2,
        selecting_non_base_fare: 3,
        higher_fare_inbound: 4,
        outbound_same_offer: 5
    }
});
export const useSplitBoundsCondition = () => {
    const { isAmerican, isRountrip } = useProfiler();
    const hasSplitBoundsCondition = isAmerican() && isRountrip();
    return {
        hasSplitBoundsCondition,
        isSplitBounds: hasSplitBoundsCondition && !!utils.trackWithDefaultStage()
    };
};
export const useTrackSplitBoundsStages = () => {
    const { hasSplitBoundsCondition } = useSplitBoundsCondition();
    const { isDesktop, isMobile, isDirect, isMeta, isLongHaul, isSolo } = useProfiler();
    const routeName = useRouteName();
    const { stages } = utils;
    const isSearchResults = routeName === "searchresults" || routeName === "searchresults-outbound" || routeName === "searchresults-inbound";
    if (isSearchResults && hasSplitBoundsCondition) {
        stages.main();
        isDesktop() && stages.desktop();
        isMobile() && stages.mobile();
        isDirect() && stages.direct();
        isMeta() && stages.meta();
        isLongHaul() && stages.Longhaul();
        isSolo() && stages.solo();
    }
};
export const trackSplitBoundsBrandedFareSelection = (params) => {
    if (!utils.trackWithDefaultStage())
        return;
    if (params.bound === "outbound" && params.index !== undefined) {
        utils.goals.selecting_non_base_fare();
    }
    if (params.bound === "inbound" && params.index !== undefined && params.index > 0) {
        utils.goals.higher_fare_inbound();
    }
};
export const useTrackSplitBoundsInboundEmptyError = (params) => {
    const bound = useGetSearchResultsBound();
    const shouldTrack = !!utils.trackWithDefaultStage() &&
        bound === "inbound" &&
        (params.fetchStatus === "failed" || (params.fetchStatus === "success" && params.visibleFlightsCount === 0));
    const previousShouldTrack = useRef(false);
    useEffect(() => {
        if (shouldTrack && !previousShouldTrack.current) {
            utils.goals.error_or_empty_inbountd();
        }
        previousShouldTrack.current = shouldTrack;
    }, [params.fetchStatus, params.flightsCount, params.visibleFlightsCount, shouldTrack]);
};
const hasMatchingLegAirline = (leftLeg, rightLeg) => {
    const leftCarrierInfo = leftLeg.flightInfo?.carrierInfo;
    const rightCarrierInfo = rightLeg.flightInfo?.carrierInfo;
    return (leftCarrierInfo?.marketingCarrier === rightCarrierInfo?.marketingCarrier ||
        leftCarrierInfo?.operatingCarrier === rightCarrierInfo?.operatingCarrier);
};
const areDuplicateOutboundOffers = (leftOffer, rightOffer) => {
    if (leftOffer.segments.length !== rightOffer.segments.length)
        return false;
    return leftOffer.segments.every((leftSegment, segmentIndex) => {
        const rightSegment = rightOffer.segments[segmentIndex];
        if (!rightSegment)
            return false;
        if (leftSegment.legs.length !== rightSegment.legs.length)
            return false;
        if (leftSegment.departureTime !== rightSegment.departureTime)
            return false;
        if (leftSegment.arrivalTime !== rightSegment.arrivalTime)
            return false;
        return leftSegment.legs.every((leftLeg, legIndex) => {
            const rightLeg = rightSegment.legs[legIndex];
            if (!rightLeg)
                return false;
            return (leftLeg.departureAirport.code === rightLeg.departureAirport.code &&
                leftLeg.arrivalAirport.code === rightLeg.arrivalAirport.code &&
                hasMatchingLegAirline(leftLeg, rightLeg));
        });
    });
};
export const trackSplitBoundsOutboundSameOffer = (offers) => {
    if (!utils.trackWithDefaultStage())
        return;
    for (let leftIndex = 0; leftIndex < offers.length; leftIndex += 1) {
        for (let rightIndex = leftIndex + 1; rightIndex < offers.length; rightIndex += 1) {
            if (areDuplicateOutboundOffers(offers[leftIndex], offers[rightIndex])) {
                utils.goals.outbound_same_offer();
                return;
            }
        }
    }
};
const key = "split_bounds_tracking";
const getTrackingData = () => flightsSessionStore.get(key) || {};
const addTokenTrackingValue = (token, type) => {
    const data = getTrackingData();
    const tokenData = data[token] || {};
    flightsSessionStore.set(key, {
        ...data,
        [token]: { ...tokenData, [type]: true }
    }, { ttl: "5m" });
};
const getTokenTrackingValue = (token) => {
    const data = getTrackingData();
    return data[token];
};
export const splitBoundsTrackingStorage = {
    addValue: addTokenTrackingValue,
    getValue: getTokenTrackingValue
};
export default utils;
